import { Breadcrumbs, Stack, SxProps, Theme, Typography } from '@mui/material';
import { BackButton } from 'common/ui/BackButton';
import { CSSProperties, PropsWithChildren } from 'react';
import { RouterLink } from 'common/ui/RouterLink';
import { ItemSeparator } from 'common/ui/ItemSeparator';

export type HeaderBreadcrumb =
  | { name: string; path: string }
  | { name: string; action: () => void };

type HeaderProps = PropsWithChildren<{
  sx?: SxProps;
  breadcrumbs?: HeaderBreadcrumb[];
  hideBreadcrumbs?: boolean;
  currentBreadcrumb: string;
  title: string;
  subtitle?: string | React.ReactNode | null;
  items?: (string | React.ReactNode)[];
  actions?: React.ReactNode;
  onBack?: () => void;
}>;

const containerStyle: SxProps<Theme> = {
  py: 0,
  pb: '20px'
};

const titleStyle: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: 48,
  lineHeight: '60px',
  letterSpacing: '-2%',
  p: 0
};

const breadcrumbActionStyle: CSSProperties = {
  cursor: 'pointer'
};

const currentBreadcrumbStyle: SxProps<Theme> = {
  fontSize: '12px',
  color: 'customGrey.dark'
};

const contentRowStyle = (hasBackOrActions: boolean): SxProps<Theme> => ({
  mt: hasBackOrActions ? '10px' : '0'
});

const backButtonContainerStyle: SxProps = {
  mt: 0
};

const actionsRowStyle: SxProps<Theme> = {
  flex: 1,
  justifyContent: 'flex-end'
};

export const Header = ({
  sx,
  hideBreadcrumbs,
  breadcrumbs = [],
  currentBreadcrumb,
  title,
  subtitle,
  items,
  children,
  actions,
  onBack
}: HeaderProps) => {
  const filteredItems = items?.filter(Boolean);

  return (
    <Stack sx={{ ...containerStyle, ...sx }} gap="8px">
      {!hideBreadcrumbs && (
        <Breadcrumbs aria-label="breadcrumb">
          {breadcrumbs.map((breadcrumb, index) => {
            if (typeof breadcrumb === 'object' && 'path' in breadcrumb) {
              return (
                <RouterLink key={index} to={breadcrumb.path}>
                  {breadcrumb.name}
                </RouterLink>
              );
            }
            return (
              <span
                key={index}
                onClick={breadcrumb.action}
                style={breadcrumbActionStyle}
              >
                {breadcrumb.name}
              </span>
            );
          })}
          <Typography sx={currentBreadcrumbStyle}>
            {currentBreadcrumb}
          </Typography>
        </Breadcrumbs>
      )}
      <Typography sx={titleStyle}>{title}</Typography>
      {subtitle && <Typography variant="subTitle">{subtitle}</Typography>}
      {filteredItems && <ItemSeparator items={filteredItems} />}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={contentRowStyle(Boolean(onBack || actions))}
      >
        {(onBack || actions) && (
          <Stack
            width="100%"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="8px"
          >
            {onBack && (
              <BackButton
                onClick={onBack}
                containerStyle={backButtonContainerStyle}
              />
            )}
            <Stack direction="row" gap="8px" sx={actionsRowStyle}>
              {actions}
            </Stack>
          </Stack>
        )}
        {children}
      </Stack>
    </Stack>
  );
};
